import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  Menu,
  MenuItem,
  IconButton,
  Box,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faBan,
  faArrowLeft,
  faArrowRight,
  faEquals,
  faNotEqual,
  faLessThan,
  faLessThanEqual,
  faGreaterThan,
  faGreaterThanEqual,
  faExchangeAlt,
} from "@fortawesome/free-solid-svg-icons";
import { debounce } from "lodash";

const filterOptions = [
  { label: "Contains", value: "contains", icon: <FontAwesomeIcon icon={faSearch} /> },
  { label: "Not Contains", value: "notContains", icon: <FontAwesomeIcon icon={faBan} /> },
  { label: "Starts With", value: "startsWith", icon: <FontAwesomeIcon icon={faArrowRight} /> },
  { label: "Ends With", value: "endsWith", icon: <FontAwesomeIcon icon={faArrowLeft} /> },
  { label: "Equals", value: "equals", icon: <FontAwesomeIcon icon={faEquals} /> },
  { label: "Different", value: "different", icon: <FontAwesomeIcon icon={faNotEqual} /> },
  { label: "Greater Than", value: "greaterThan", icon: <FontAwesomeIcon icon={faGreaterThan} /> },
  { label: "Greater Than or Equal", value: "greaterThanOrEqual", icon: <FontAwesomeIcon icon={faGreaterThanEqual} /> },
  { label: "Less Than", value: "lessThan", icon: <FontAwesomeIcon icon={faLessThan} /> },
  { label: "Less Than or Equal", value: "lessThanOrEqual", icon: <FontAwesomeIcon icon={faLessThanEqual} /> },
  { label: "Between", value: "between", icon: <FontAwesomeIcon icon={faExchangeAlt} /> },
];

const DEBOUNCE_DELAY = 300; // in milliseconds

const ColumnFilter = ({ field, value, fieldType, onFilterChange, headerName, hiddenFilterOptions = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(fieldType === "date"?'between':filterOptions[0].value);
  const [inputValue, setInputValue] = useState("");
  const [rangeValues, setRangeValues] = useState({ value1: "", value2: "" });




  const debouncedFilterChange = useCallback(
    () =>
      debounce((newValue) => {
        onFilterChange(field, { type: selectedFilter, value: newValue });
      }, DEBOUNCE_DELAY),
    [selectedFilter, onFilterChange, field]
  );


  useEffect(() => {
      const debounced = debouncedFilterChange()
    return () => {
      debounced.cancel();
    };
  }, [debouncedFilterChange]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFilterSelect = (filterType) => {
    setSelectedFilter(filterType);
    handleMenuClose();
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    debouncedFilterChange()(newValue); //Call debounced func returned from useCallback
  };

  const handleRangeChange = (e) => {
    const { name, value } = e.target;
    const updatedRange = { ...rangeValues, [name]: value };
    setRangeValues(updatedRange);
    debouncedFilterChange()(updatedRange); //Call debounced func returned from useCallback
  };

  const selectedIcon = filterOptions.find(
    (option) => option.value === selectedFilter
  )?.icon;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {!hiddenFilterOptions && <IconButton
        size="small"
        onClick={handleMenuOpen}
        aria-controls="filter-menu"
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl) ? "true" : undefined}
      >
        {selectedIcon || <FilterAltIcon />}
      </IconButton>}
      <Menu
        id="filter-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        MenuListProps={{
          "aria-labelledby": "filter-menu-button",
        }}
      >
        {filterOptions.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === selectedFilter}
            onClick={() => handleFilterSelect(option.value)}
          >
            {option.icon}   {option.label}
          </MenuItem>
        ))}
      </Menu>
      {selectedFilter === "between" ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
          <TextField
            type={fieldType === "date" ? "date" : "text"}
            variant="standard"
            size="small"
            placeholder="Start value"
            name="value1"
            value={rangeValues.value1}
            onChange={handleRangeChange}
            sx={{ width: "100%" }}
          />
          <TextField
            type={fieldType === "date" ? "date" : "text"}
            variant="standard"
            size="small"
            placeholder="End value"
            name="value2"
            value={rangeValues.value2}
            onChange={handleRangeChange}
            sx={{ width: "100%" }}
          />
        </Box>
      ) : (
        <TextField
          type={fieldType === "date" ? "date" : "text"}
          variant="standard"
          size="small"
          placeholder={`Filter ${headerName}`}
          value={inputValue}
          onChange={handleInputChange}
          sx={{ width: "100%" }}
        />
      )}
    </Box>
  );
};

export default ColumnFilter;